import { FC, useMemo } from 'react';
import { Button, Empty, Table } from 'antd';
import { useTranslate } from '@refinedev/core';
import { EyeOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import tableStyles from '../tableStyles.module.scss';
import TablePagination from '../../../ui/table-pagination';
import { useLocalPagination } from '../../../../hooks/useLocalPagination';

interface DocumentHistoryProps {
    data: Column[];
    group: string;
}

interface Column {
    id: number;
    name: string;
    code: string;
    created: string;
    url: string;
}

export const DocumentHistory: FC<DocumentHistoryProps> = ({ data, group }) => {
    const t = useTranslate();

    const handleOpenDocument = (record: Column) => {
        window.open(
            record.url,
            'documentWindow',
            'left=20,top=20,width=984,height=675,toolbar=1,resizable=0'
        );

        return false;
    };

    const columns: ColumnsType<Column> = [
        {
            title: t(`${group}.name`),
            dataIndex: 'name',
            width: '35%',
        },
        {
            title: t(`${group}.date`),
            dataIndex: 'created',
            width: '35%',
        },
        {
            title: t(`${group}.code`),
            dataIndex: 'code',
            width: '20%',
        },
        {
            title: t(`${group}.details`),
            dataIndex: 'details',
            render: (value: string, record: Column) => {
                return (
                    <Button
                        // eslint-disable-next-line no-script-url
                        href="javascript:void(0)"
                        onClick={() => handleOpenDocument(record)}
                        icon={<EyeOutlined />}
                    >
                        {t('applications.titles.view')}
                    </Button>
                );
            },
        },
    ];

    const normalizedData = useMemo(
        () => (Array.isArray(data) ? data : []),
        [data]
    );

    const {
        currentPage,
        pageSize,
        pageSizeOptions,
        onChangePage,
        onChangePageSize,
        startIndex,
        endIndex,
    } = useLocalPagination(normalizedData.length);

    const paginatedData = useMemo(
        () => normalizedData.slice(startIndex, endIndex),
        [normalizedData, startIndex, endIndex]
    );

    if (normalizedData.length === 0) {
        return <Empty description={t('applications.titles.no_data')} />;
    }

    return (
        <div className={tableStyles.tableWrapper}>
            <Table<Column>
                columns={columns}
                dataSource={paginatedData}
                rowKey="name"
                pagination={false}
                className={tableStyles.table}
            />
            <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={normalizedData.length}
                pageSizeOptions={pageSizeOptions}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
                previousLabel={t(
                    'applications.pagination.previous',
                    'Попередня сторінка'
                )}
                nextLabel={t(
                    'applications.pagination.next',
                    'Наступна сторінка'
                )}
                showDocumentsLabel={t(
                    'applications.pagination.show_documents',
                    'Показувати документи'
                )}
                rangeLabel={(start, end, total) =>
                    `${start}-${end} ${t(
                        'applications.pagination.of_total',
                        'з'
                    )} ${total}`
                }
            />
        </div>
    );
};
